export const RT4K_PROFILE_DATA_OFFSET = 0x80;
export const RT4K_MODE_PAD = 128;
export const RT4K_PORT_PAD = 8;
export const RT4K_OPT_PAD = 32;
export const RT4K_MAX_FILE_LEN = 260;
export const RT4K_BFI_MAX = 16;
export const RT4K_FILE_PATH_BYTES = RT4K_MAX_FILE_LEN * 2;

// Profiles serialize the firmware state struct after the 0x80-byte header.
// Field offsets follow Struct v3 (firmware 1.80); the total size is unchanged.
// The embedded ABI stores the C "double" field in this struct as four bytes.
export const RT4K_STATE_SIZE_196 = 0x595c;

export interface Rt4kStructField {
  offset: number;
  size: number;
  elementSize: number;
  count: number;
}

export const RT4K_STATE_FIELDS = {
  crt_beam_func: { offset: 0x0000, size: 4, elementSize: 4, count: 1 },
  crt_mask_en: { offset: 0x000c, size: 4, elementSize: 4, count: 1 },
  crt_mask_file: { offset: 0x0010, size: RT4K_FILE_PATH_BYTES, elementSize: 1, count: RT4K_FILE_PATH_BYTES },
  crt_mask_str: { offset: 0x0220, size: 4, elementSize: 4, count: 1 },
  output_frame_nominal: { offset: 0x0320, size: 4, elementSize: 4, count: 1 },
  scaler_trim_right: { offset: 0x0324, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  scaler_trim_left: { offset: 0x0424, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  scaler_trim_top: { offset: 0x0524, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  scaler_trim_bottom: { offset: 0x0624, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  scaler_pre_scale: { offset: 0x0724, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  scaler_ar_mode: { offset: 0x07a4, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  scaler_mode: { offset: 0x0824, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  scaler_vert_ar_factor: { offset: 0x08a4, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  scaler_hori_free_factor: { offset: 0x09a4, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  scaler_vert_free_factor: { offset: 0x0aa4, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  scaler_hori_kernel: { offset: 0x0ba4, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  scaler_vert_kernel: { offset: 0x0c24, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  tvp_sr_override: { offset: 0x0cf8, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  tvp_dec_factor: { offset: 0x0df8, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  tvp_dec_phase: { offset: 0x0e78, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  tvp_phase_override: { offset: 0x0ef8, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  tvp_aa_filter: { offset: 0x0f78, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  tvp_sog_override: { offset: 0x0ff8, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  tvp_pre_coast_override: { offset: 0x1078, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  tvp_post_coast_override: { offset: 0x10f8, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  tvp_pre_gain_override: { offset: 0x1178, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  tvp_gain_r_override: { offset: 0x1278, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  tvp_gain_g_override: { offset: 0x1378, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  tvp_gain_b_override: { offset: 0x1478, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  audio_gain: { offset: 0x158c, size: RT4K_PORT_PAD, elementSize: 1, count: RT4K_PORT_PAD },
  audio_override: { offset: 0x1594, size: RT4K_PORT_PAD, elementSize: 1, count: RT4K_PORT_PAD },
  audio_swap: { offset: 0x159c, size: RT4K_PORT_PAD, elementSize: 1, count: RT4K_PORT_PAD },
  audio_sr: { offset: 0x15a4, size: 1, elementSize: 1, count: 1 },
  opt_dec_factor_en: { offset: 0x1bac, size: RT4K_OPT_PAD, elementSize: 1, count: RT4K_OPT_PAD },
  opt_sample_det_en: { offset: 0x1bcc, size: 4, elementSize: 4, count: 1 },
  opt_auto_phase_en: { offset: 0x1bd0, size: 4, elementSize: 4, count: 1 },
  opt_tvp_adc_phase: { offset: 0x1bd4, size: RT4K_OPT_PAD, elementSize: 1, count: RT4K_OPT_PAD },
  opt_tvp_dec_phase: { offset: 0x1bf4, size: RT4K_OPT_PAD, elementSize: 1, count: RT4K_OPT_PAD },
  opt_scaler_trim_right: { offset: 0x1c14, size: RT4K_OPT_PAD * 2, elementSize: 2, count: RT4K_OPT_PAD },
  opt_scaler_trim_left: { offset: 0x1c54, size: RT4K_OPT_PAD * 2, elementSize: 2, count: RT4K_OPT_PAD },
  opt_scaler_trim_top: { offset: 0x1c94, size: RT4K_OPT_PAD * 2, elementSize: 2, count: RT4K_OPT_PAD },
  opt_scaler_trim_bottom: { offset: 0x1cd4, size: RT4K_OPT_PAD * 2, elementSize: 2, count: RT4K_OPT_PAD },
  opt_scaler_ar_mode: { offset: 0x1d14, size: RT4K_OPT_PAD, elementSize: 1, count: RT4K_OPT_PAD },
  opt_scaler_mode: { offset: 0x1d34, size: RT4K_OPT_PAD, elementSize: 1, count: RT4K_OPT_PAD },
  opt_scaler_vert_ar_factor: { offset: 0x1d54, size: RT4K_OPT_PAD * 2, elementSize: 2, count: RT4K_OPT_PAD },
  opt_scaler_hori_free_factor: { offset: 0x1d94, size: RT4K_OPT_PAD * 2, elementSize: 2, count: RT4K_OPT_PAD },
  opt_scaler_vert_free_factor: { offset: 0x1dd4, size: RT4K_OPT_PAD * 2, elementSize: 2, count: RT4K_OPT_PAD },
  input_gamma_sdr_csc: { offset: 0x1e5c, size: 4, elementSize: 4, count: 1 },
  output_gamma_sdr_csc: { offset: 0x1e60, size: 4, elementSize: 4, count: 1 },
  auto_dv1_profile: { offset: 0x1eac, size: 4, elementSize: 4, count: 1 },
  auto_svs_profile: { offset: 0x1eb8, size: 4, elementSize: 4, count: 1 },
  hdmi_dec_num: { offset: 0x1808, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  tvp_off_r_override: { offset: 0x18b0, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  tvp_off_g_override: { offset: 0x19b0, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  tvp_off_b_override: { offset: 0x1ab0, size: RT4K_MODE_PAD * 2, elementSize: 2, count: RT4K_MODE_PAD },
  audio_bal: { offset: 0x1ec4, size: RT4K_PORT_PAD, elementSize: 1, count: RT4K_PORT_PAD },
  e_scanline_gain: { offset: 0x1ee8, size: 2, elementSize: 2, count: 1 },
  e_scanline_spread: { offset: 0x1eea, size: 2, elementSize: 2, count: 1 },
  custom_slot_file: { offset: 0x1ef4, size: RT4K_FILE_PATH_BYTES * 2, elementSize: RT4K_FILE_PATH_BYTES, count: 2 },
  scaler_vert_pan: { offset: 0x2304, size: RT4K_MODE_PAD, elementSize: 1, count: RT4K_MODE_PAD },
  profile_created_on: { offset: 0x2384, size: 8, elementSize: 4, count: 2 },
  resampler_mode: { offset: 0x239e, size: 1, elementSize: 1, count: 1 },
  native_sample_en: { offset: 0x23a0, size: 1, elementSize: 1, count: 1 },
  lumacode_en: { offset: 0x23a4, size: 1, elementSize: 1, count: 1 },
  lumacode_file: { offset: 0x23a8, size: RT4K_FILE_PATH_BYTES, elementSize: 1, count: RT4K_FILE_PATH_BYTES },
  hdmi_bfi_blur: { offset: 0x0264, size: RT4K_BFI_MAX * 4, elementSize: 4, count: RT4K_BFI_MAX },
  hdmi_bfi_strobe: { offset: 0x02a4, size: RT4K_BFI_MAX * 4, elementSize: 4, count: RT4K_BFI_MAX },
  osd_banner_image_file: { offset: 0x15c4, size: RT4K_FILE_PATH_BYTES, elementSize: 1, count: RT4K_FILE_PATH_BYTES },
  input_source: { offset: 0x57e9, size: 1, elementSize: 1, count: 1 },
  input_ic: { offset: 0x57ea, size: 1, elementSize: 1, count: 1 },
  input_port: { offset: 0x57eb, size: 1, elementSize: 1, count: 1 },
} as const satisfies Record<string, Rt4kStructField>;

export type Rt4kStructFieldName = keyof typeof RT4K_STATE_FIELDS;

export function rt4kFieldOffset(fieldName: Rt4kStructFieldName): number {
  return RT4K_PROFILE_DATA_OFFSET + RT4K_STATE_FIELDS[fieldName].offset;
}

export function rt4kArrayElementOffset(fieldName: Rt4kStructFieldName, index: number): number {
  const field = RT4K_STATE_FIELDS[fieldName];

  if (index < 0 || index >= field.count) {
    throw new RangeError(`${fieldName}[${index}] is outside the ${field.count}-element field.`);
  }

  return rt4kFieldOffset(fieldName) + index * field.elementSize;
}
